<template>
    <Progressbar v-if="loading" />
    <NetworkError v-else-if="!person.first_name && errors" @tryAgain="getData" />
    <div class="w-full h-full bg-gray-200 dark:bg-base-100 rounded-tr-3xl xl:px-9 pt-6 lg:pt-10 pb-14 " v-else>
        <div class="container">
            <div class="flex justify-between items-center mb-6">
                <h3 class="flex items-center md:text-26 text-2xl dark:text-white text-biscay-700 font-semibold">
                    <i class="md:flex hidden w-2 h-2 dark:bg-white rounded-full bg-blue-700 ml-2"></i>
                    {{ __('Person details') }}
                </h3>
                <div class="flex justify-end order-first lg:order-last">
                    <Dropdown labelClass="btn-outline !btn-sm">
                        <ul class="menu dropdown-content bg-base-100 rounded-box z-[2] w-36 p-2 shadow lg:!end-auto">
                            <li>
                                <router-link :to="{ name: 'user.person.edit', params: { id: person.id } }"
                                    :class="{'btn-disabled opacity-50': !can('person.edit.all') && !can('person.edit.self', person.self)}">
                                    {{ __('Edit') }}
                                </router-link>
                            </li>
                            <li v-if="can('appointment.create')">
                                <button @click="setAppointment('create')">
                                    {{ __('Create activity') }}
                                </button>
                            </li>
                            <li v-if="can('invoice.create')">
                                <button @click="setInvoice('create')">
                                    {{ __('Add invoice') }}
                                </button>
                            </li>
                        </ul>
                    </Dropdown>
                    <label @click="setItem(person)" for="person-delete-modal"
                        :class="{ 'btn-disabled': !can('person.delete.all') && !can('person.delete.self', person.self) }"
                        class="btn btn-outline btn-error btn-sm mr-2">
                        {{ __('Delete') }}
                    </label>
                </div>
            </div>
            <div class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse">
                <input type="checkbox" checked />
                <div class="collapse-title text-xl font-medium">
                    {{ __('personal information') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Full name') }}: <span class="text-black dark:text-white">{{
                            (person.salutation ? __(person.salutation?.capitalize()) + ' ' : '') + person.name
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Side') }}: <span class="text-black dark:text-white">{{
                            person.title || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Account name') }}:
                            <router-link v-if="person.account?.id"
                                :to="{ name: 'user.account.show', params: { id: person.account_id } }"
                                class="text-blue-500 hover:text-blue-600">{{
                                    person.account?.name
                                }}</router-link>
                            <span v-else class="text-black dark:text-white">{{
                                person.account_id ? (person.account?.name ?? __('Deleted')) : '---'
                            }}</span>
                        </p>
                        <p class="text-gray-70">{{ __('Lead source') }}: <span class="text-black dark:text-white">{{
                            person.lead_source || '---' }}</span></p>
                        <p class="text-gray-70" v-if="person.emails.filter(item => item.value).length">{{ __('Email')
                            }}: <span class="text-black dark:text-white"
                                v-for="item in person.emails.filter(item => item.value)">{{
                                    item.value
                                }}</span></p>
                        <p class="text-gray-70">{{ __('User') }}: <span class="text-black dark:text-white">{{
                            person.user }}</span></p>
                        <p class="text-gray-70">{{ __('Created At') }}: <span class="text-black dark:text-white">{{
                            person.created_at }}</span></p>
                        <p class="text-gray-70">{{ __('Update date') }}: <span class="text-black dark:text-white">{{
                            person.updated_at }}</span></p>
                        <div class="divider h-auto col-span-full my-0"
                            v-if="person.numbers.filter(item => item.value).length"></div>
                        <p class="text-gray-70" v-for="item in person.numbers.filter(item => item.value)">{{
                            __(item.type.capitalize()) }}: <span class="text-black dark:text-white">{{ item.value
                                }}</span></p>
                        <div class="divider h-auto col-span-full my-0"></div>
                        <p class="text-gray-70">{{ __('Description') }}: <span class="text-black dark:text-white">{{
                            person.description || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('More information') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Side') }}: <span class="text-black dark:text-white">{{
                            person.title || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Manager name') }}: <span class="text-black dark:text-white">{{
                            person.report_to_name || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Campaign') }}: <span class="text-black dark:text-white">{{
                            person.campaign_name || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('National number') }}: <span class="text-black dark:text-white">{{
                            person.national_number || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Website') }}: <span class="text-black dark:text-white">{{
                            person.website || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Department') }}: <span class="text-black dark:text-white">{{
                            person.department || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Secretary name') }}: <span class="text-black dark:text-white">{{
                            person.assistant || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Date of birth') }}: <span class="text-black dark:text-white">{{
                            person.birthdate || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Address') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70">{{ __('Country') }}: <span class="text-black dark:text-white">{{
                            person.billing.country || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('State') }}: <span class="text-black dark:text-white">{{
                            person.billing.state || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('City') }}: <span class="text-black dark:text-white">{{
                            person.billing.city || '---'
                                }}</span></p>
                        <p class="text-gray-70">{{ __('Postal Code') }}: <span class="text-black dark:text-white">{{
                            person.billing.postalcode || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Address') }}: <span class="text-black dark:text-white">{{
                            person.billing.street || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Latitude') }}: <span class="text-black dark:text-white">{{
                            person.billing.latitude || '---' }}</span></p>
                        <p class="text-gray-70">{{ __('Longitude') }}: <span class="text-black dark:text-white">{{
                            person.billing.longitude || '---' }}</span></p>
                    </div>
                </div>
            </div>
            <div v-if="person.social.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Social') }}
                </div>
                <div class="collapse-content">
                    <div class="grid md:grid-cols-2 grid-cols-1 lg:grid-cols-3 md:gap-x-14 gap-y-4">
                        <p class="text-gray-70" v-for="(item, key) in person.social">{{ __(key?.capitalize()) }}: <span
                                class="text-black dark:text-white">{{
                                    item || '---'
                                }}</span></p>
                    </div>
                </div>
            </div>
            <div v-if="person.appointments?.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Activities') }}
                </div>
                <div class="collapse-content">
                    <TableAppointment :appointments="person.appointments" @setItem="setItem" />
                </div>
            </div>
            <div v-if="person.invoices?.length"
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse-plus collapse mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Invoices') }}
                </div>
                <div class="collapse-content">
                    <TableInvoice :invoices="person.invoices" @setItem="setItem" />
                </div>
            </div>
            <div
                class="bg-white dark:bg-base-300 dark:shadow-whiteShadow rounded-xl shadow-sm collapse collapse-plus mt-4">
                <input type="checkbox" />
                <div class="collapse-title text-xl font-medium">
                    {{ __('Appendices') }}
                </div>
                <div class="collapse-content">
                    <div class="overflow-x-auto">
                        <table class="table table-xs md:table-sm xl:table-md">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{{ __('File title') }}</th>
                                    <th>{{ __('File type') }}</th>
                                    <th>{{ __('Status') }}</th>
                                    <th>{{ __('Action') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in person.attachments">
                                    <th>{{ index + 1 }}</th>
                                    <th>{{ item.name }}</th>
                                    <td>{{ item.mime_type }}</td>
                                    <td>
                                        {{ __(item.url ? 'Uploaded' : 'Uploading') }}
                                    </td>
                                    <td>
                                        <a :href="item.url" target="_blank" class="btn btn-outline btn-info btn-sm">
                                            {{ __('Show') }}
                                        </a>
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot class="text-center" v-if="!person.attachments.length">
                                <tr>
                                    <td colspan="5">{{ __('No file found to display') }}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
     <Teleport to="#app">
        <input type="checkbox" id="add-appointment-modal" class="modal-toggle" />
        <div class="modal" for="add-appointment-modal">
            <div class="modal-box max-w-7xl bg-base-200 min-h-40" for="">
                <label @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</label>
                <AddEditAppointment :appointment="appointment" v-if="modal == 'add-appointment-modal'"
                    modal="add-appointment-modal" />
            </div>
        </div>
    </Teleport>

    <Teleport to="#app">
        <input type="checkbox" id="add-invoice-modal" class="modal-toggle" />
        <div class="modal" for="add-invoice-modal">
            <div class="modal-box max-w-7xl bg-base-200 min-h-40" for="">
                <label @click="hideModal" class="btn btn-sm btn-circle btn-ghost absolute left-2 top-2">✕</label>
                <AddEditInvoice :invoice="invoice" v-if="modal == 'add-invoice-modal'" modal="add-invoice-modal" />
            </div>
        </div>
    </Teleport>

    <ModalDelete id="person-delete-modal" :title='__("Delete person")'
        :content='__("Are you sure to delete this person?")' @remove="removePerson" />

    <ModalDelete id="appointment-delete-modal" :title="__('Delete appointment')"
        :content="__('Are you sure to delete this appointment?')" @remove="removeAppointmentItem" />

    <ModalDelete id="invoice-delete-modal" :title="__('Delete invoice')"
        :content="__('Are you sure to delete this invoice?')" @remove="removeInvoiceItem" />
</template>
<script setup>

import { computed, onMounted, reactive, ref } from 'vue';
const { getPerson, person, route, loading, meta, errors, remove, removePerson,removeAppointment, removeInvoice, appointment, invoice, loadingBtn } = useDashboard();
const setItem = (item) => Object.assign(remove, { id: item.id, single: true });
const modal = ref(null);
const getData = () => {
    getPerson();
}

const setAppointment = () => {
    Object.assign(appointment, {
        parent_type: "person",
        appointmentable_id: person.id,
    });
    modal.value = 'add-appointment-modal';
    document.getElementById(modal.value).checked = true;
}
const removeAppointmentItem = () => {
    removeAppointment('appointment-delete-modal').then((res) => {
        if (res && !res.error) getData();
    });
}
const removeInvoiceItem = () => {
    removeInvoice('invoice-delete-modal').then((res) => {
        if (res && !res.error) getData();
    });
}
watch(progressDialog, (value) => {
    if (modal.value && !value) {
        modal.value = null;
        getData();
    }
})
const setInvoice = () => {
    Object.assign(invoice, {
        parent_type: "person",
        invoiceable_id: person.id,
    });
    modal.value = 'add-invoice-modal';
    document.getElementById(modal.value).checked = true;
}
const hideModal = () => {
    document.getElementById(modal.value).checked = false;
    modal.value = null;
}
onMounted(() => {
    getData();
});
</script>